import React, { useEffect, useRef, useState } from "react";
import "./WorkspacePresetPicker.css";

// "Workspace <preset>" pill that opens an inline layout switcher. Shared by the
// Product Dashboard (monitors) and both CoTrader desks; expects --ct-* tokens
// (rendered inside .ct-page).
const WorkspacePresetPicker = ({
  presets,
  activePreset,
  label,
  onSelect,
  onMoreOptions,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="ws-preset" ref={rootRef}>
      <button
        type="button"
        className="ws-preset-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch workspace layout"
      >
        Workspace <strong>{label}</strong>
        <i className="fa-solid fa-chevron-down ws-preset-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="ws-preset-menu" role="menu" aria-label="Workspace layouts">
          {Object.entries(presets).map(([presetKey, presetValue]) => {
            const isActive = activePreset === presetKey;
            return (
              <button
                key={presetKey}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                className={`ws-preset-option${isActive ? " is-active" : ""}`}
                onClick={() => {
                  if (!isActive) onSelect(presetKey);
                  setOpen(false);
                }}
              >
                <span className="ws-preset-text">
                  <span className="ws-preset-label">{presetValue.label}</span>
                  <span className="ws-preset-desc">{presetValue.description}</span>
                </span>
                {isActive && (
                  <i className="fa-solid fa-check ws-preset-check" aria-hidden="true" />
                )}
              </button>
            );
          })}
          {onMoreOptions && (
            <div className="ws-preset-footer">
              <button
                type="button"
                className="ws-preset-more"
                onClick={() => {
                  setOpen(false);
                  onMoreOptions();
                }}
              >
                <i className="fa-solid fa-sliders" aria-hidden="true" />
                More layout options
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkspacePresetPicker;
